import {
    TokenAmount,
    TransactionDetailRow,
    TransactionFeeSection,
    TransactionDialogContent,
    TransactionSection,
    TransactionArrow,
} from "@/components";
import { useMintTransaction } from "@/hooks";
import { TOKEN_IDS, type TokenId } from "@/types";
import { getGatewayContractAddress } from "@/config";
import { useTokenApprovalV2 } from "@/hooks/transaction/useTokenApprovalV2";
import { GENERIC_TRANSACTION_EVENTS, genericTransactionEventEmitter } from "@/events";
import { mainnet } from "viem/chains";
import { ActionText } from "@/constants/text.enum";
import { safaryTrackMintSuccess } from "@/services/analytics/safary.service";
import { useAccount } from "wagmi";
import { useTrackingConsent } from "../../hooks/shared/tracking-consent/useTrackingConsent";
import { Seperator } from "../shared/Seperator";
import { getTransactionLoadingState } from "./transaction-loading.utils";

export interface MintTransactionProps {
    mintAmount: number;
    mintTokenId: TokenId;
    onClose?: () => void;
}

export function MintTransaction({ mintAmount, mintTokenId, onClose }: MintTransactionProps) {
    const { address } = useAccount();
    const {
        gasPriceUSD,
        executeMintTransaction,
        isLoading: isMintTransactionLoading,
        isExecuting,
    } = useMintTransaction({
        mintAmount,
        mintTokenId,
    });

    const DEPOSIT_TEXT = <TokenAmount amount={mintAmount} tokenId={mintTokenId} size="md" />;
    const RECEIVE_TEXT = <TokenAmount amount={mintAmount} tokenId={TOKEN_IDS.IUSD} size="md" />;
    const CONFIRM_TEXT = "Confirm Mint";

    const { isApprovalLoading, withApproval, approvalLoadingText } = useTokenApprovalV2({
        tokenId: mintTokenId,
        amount: mintAmount,
        spender: getGatewayContractAddress(mainnet.id),
    });

    const { canTrack } = useTrackingConsent();

    const handleConfirm = async () => {
        try {
            const hasExecuted = await withApproval(
                async () => {
                    await executeMintTransaction();
                    return true;
                },
                { isExecuting, amount: mintAmount },
            );

            if (!hasExecuted) return;

            canTrack &&
                address &&
                safaryTrackMintSuccess({
                    walletAddress: address,
                    tokenIn: mintTokenId,
                    tokenOut: TOKEN_IDS.IUSD,
                    iUsdMintedAmount: mintAmount,
                });

            onClose?.();
        } catch (e) {
            console.error("Error executing mint transaction:", e);
            genericTransactionEventEmitter.emit(GENERIC_TRANSACTION_EVENTS.FAILED, {
                error: `${e}`,
            });
        }
    };

    const { isLoading, loadingText } = getTransactionLoadingState({
        approvalLoadingText,
        isApprovalLoading,
        isExecuting,
        actionLoadingText: ActionText.MINTING,
        isTransactionLoading: isMintTransactionLoading,
    });
    const isDisabled = isLoading || !mintAmount || !mintTokenId;

    return (
        <TransactionDialogContent
            onClose={onClose}
            onConfirm={handleConfirm}
            isLoading={isLoading}
            loadingText={loadingText}
            confirmText={CONFIRM_TEXT}
            disabled={isDisabled}
            className="w-[420px]"
        >
            <TransactionSection>
                <TransactionDetailRow label="Mint" value={DEPOSIT_TEXT} />
                <TransactionArrow />
                <TransactionDetailRow label="" value={RECEIVE_TEXT} />
            </TransactionSection>
            <Seperator />
            <TransactionFeeSection gasPriceUSD={gasPriceUSD} />
        </TransactionDialogContent>
    );
}
